/**
 * BusinessCipSection — entity-level CIP capture surface for Business.
 *
 * Replaces the Individual CIP ID-scan panel. Business entities don't have a
 * personal photo ID; their primary identification is the Secretary of State
 * registration (Articles of Incorporation / Organization / Fictitious Name
 * Reg / Partnership Registration). Per business.md §"Identity / CIP semantics"
 * and people-form-port.md §13b.
 *
 * Core Director field mapping:
 *   - Field 254 (IdVerifyCode)    → fixed '6' (State Documents)
 *   - Field 255 (IdNumber)        → SoS charter / filing number
 *   - Field 256 (IdIssueBy)       → 2-char state code
 *   - Field 257 (IdIssueDate)     → registration / charter date
 *   - Field 258 (IdExpiryDate)    → renewal date or 00/00/0000 for perpetual
 *
 * "Search [State] SoS" button opens the StateGovSearchLinks catalog URL via
 * Teams SDK `app.openLink()` (useStateGovLink hook).
 *
 * Document capture: this section does NOT render an upload control. The
 * shared `DocumentSlotsSection` below handles the per-sub-type document grid
 * (Articles of Inc / Articles of Org / Operating Agreement / etc.) — see
 * `PRIMARY_DOC_CODE_BY_SUBTYPE` in `business-config.ts`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CollapsibleSection, Pill } from '@/components/primitives';
import { useStateGovLink } from '../shared/useStateGovLink';
import type { RaoPerson } from '../../../types';
import { PRIMARY_DOC_CODE_BY_SUBTYPE, SOS_STATE_OPTIONS } from './business-config';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers — SoS fields live on `person.extra`
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface BusinessCipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  /** Default state for the SoS state picker — normally `officer.branchStateCode`. */
  defaultStateCode?: string;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function BusinessCipSection({
  person,
  onChange,
  mode,
  isChanged,
  defaultStateCode = 'MO',
  readOnly = false,
}: BusinessCipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  // SoS-related fields live on `person.extra`. Field 256 (IdIssueBy) is the
  // 2-char state code — we store both `idState` and `idIssuer` in lock-step
  // since the Individual dialog writes `idIssuer` for Core Director F256.
  const sosRegNumber   = readExtra(person, 'idNumber');
  const sosStateCode   = readExtra(person, 'idState') || readExtra(person, 'idIssuer') || defaultStateCode;
  const sosRegDate     = readExtra(person, 'idIssueDate');
  const sosRenewalDate = readExtra(person, 'idExpiryDate');

  const subType = (person.customerType ?? '') as string;
  const expectedDocCode = PRIMARY_DOC_CODE_BY_SUBTYPE[subType];

  // Pin Field 254 = '6' (State Documents) for all Business entities. Mirrors
  // the Individual CIP section's behavior of writing the ID type code.
  useEffect(() => {
    if (readOnly) return;
    if (readExtra(person, 'idType') !== '6') {
      onChange(patchExtra(person, { idType: '6' }));
    }
    // Keep idState + idIssuer in sync so the save dispatcher only needs to
    // read one of them.
    if (sosStateCode && readExtra(person, 'idIssuer') !== sosStateCode) {
      onChange(patchExtra(person, { idIssuer: sosStateCode, idState: sosStateCode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, sosStateCode]);

  const { link, loading: linkLoading, openInBrowser } = useStateGovLink(sosStateCode, 'Business');

  const expandedInitial = mode === 'new' || !sosRegNumber;

  // Toast state — inline auto-dismissing confirmation after SoS click.
  // No Toast primitive exists in @/components/primitives (checked 2026-04-24),
  // so we render a local positioned div driven by useState + a timer.
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  function showToast(message: string): void {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
      toastTimerRef.current = null;
    }, 3000);
  }

  // Legal entity name occupies firstName + lastName slots (Core Director
  // Fields 6+7) — see BusinessIdentitySection for the canonical join.
  const legalEntityName = [person.firstName ?? '', person.lastName ?? '']
    .filter(Boolean)
    .join(' ')
    .trim();
  const ein = (person.taxId ?? '').trim();

  const handleSearchSos = useCallback((): void => {
    // Best-effort clipboard copy; never block the link open.
    const tryCopy = async (): Promise<void> => {
      if (!legalEntityName) return;
      try {
        const clip =
          typeof navigator !== 'undefined'
            ? (navigator as Navigator & { clipboard?: { writeText?: (t: string) => Promise<void> } }).clipboard
            : undefined;
        if (clip && typeof clip.writeText === 'function') {
          // Copy name (+ EIN if present) as plain text. Two-line format when
          // EIN is populated — officer can paste either into the SoS form.
          const payload = ein ? `${legalEntityName}\nEIN ${ein}` : legalEntityName;
          await clip.writeText(payload);
          if (ein) {
            showToast(`Copied: ${legalEntityName} + EIN ${ein}. Use either in SoS search.`);
          } else {
            showToast('Business name copied — paste in SoS search');
          }
        }
      } catch {
        // Clipboard unavailable (older WebView2) or threw — silently skip.
      }
    };

    void tryCopy();
    openInBrowser();
  }, [legalEntityName, ein, openInBrowser]);

  function onStateChange(next: string): void {
    onChange(patchExtra(person, { idState: next, idIssuer: next }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <CollapsibleSection
        title="Secretary of State Registration (Entity CIP)"
        iconColor="var(--pcb-red, #b10505)"
        defaultOpen={expandedInitial}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.5 }}>
            Business-entity CIP uses the Secretary of State registration instead
            of a personal photo ID. ID Type (Field 254) is set to{' '}
            <Pill variant="info">6 · State Documents</Pill>. Upload the registration
            PDF in the documents section below.
            {expectedDocCode && (
              <>
                {' '}Expected primary doc slot for <strong>{subType}</strong>:{' '}
                <Pill variant="muted">{expectedDocCode}</Pill>
              </>
            )}
          </div>

          {/* State + Lookup button */}
          <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle} htmlFor="rao-biz-sos-state">
                SoS State (Field 256) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
              </label>
              <select
                id="rao-biz-sos-state"
                value={sosStateCode}
                onChange={(e) => onStateChange(e.currentTarget.value)}
                disabled={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idState') || ic('idIssuer'))}
                title="State where the entity is registered"
              >
                {SOS_STATE_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ position: 'relative' }}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSearchSos}
                  disabled={!link || linkLoading || readOnly}
                  title={
                    link
                      ? `Copies the business name to your clipboard, then opens ${link.displayLabel} in your default browser`
                      : linkLoading
                        ? 'Loading state-gov search link…'
                        : `No StateGovSearchLinks catalog row for (${sosStateCode}, Business)`
                  }
                >
                  {linkLoading ? 'Loading…' : `Search ${sosStateCode} SoS`}
                </Button>
                {toastMessage && (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      maxWidth: 360,
                      padding: '8px 12px',
                      background: 'var(--brand-fill-strong)',
                      color: 'var(--on-brand)',
                      borderRadius: 6,
                      fontSize: 12,
                      lineHeight: 1.4,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
                      whiteSpace: 'pre-line',
                      zIndex: 10,
                      pointerEvents: 'none',
                    }}
                  >
                    {toastMessage}
                  </div>
                )}
              </div>
              {!link && !linkLoading && (
                <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 4 }}>
                  No catalog row for this state. Wire via <code>/v3/config/state-gov-search-links</code> seed.
                </div>
              )}
            </div>
          </div>

          {/* Registration # + dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-biz-sos-regnum">
                SoS Registration # (Field 255) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
              </label>
              <input
                id="rao-biz-sos-regnum"
                type="text"
                value={sosRegNumber}
                onChange={(e) => onChange(patchExtra(person, { idNumber: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idNumber'))}
                placeholder="LC014679987"
                maxLength={30}
                title="SoS charter / filing number (e.g., LC014679987 for MO LLC)"
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-biz-sos-regdate">
                Registration Date (Field 257)
              </label>
              <input
                id="rao-biz-sos-regdate"
                type="date"
                value={sosRegDate}
                onChange={(e) => onChange(patchExtra(person, { idIssueDate: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idIssueDate'))}
                title="State registration / charter date"
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-biz-sos-renewal">
                Renewal / Expiry (Field 258)
              </label>
              <input
                id="rao-biz-sos-renewal"
                type="date"
                value={sosRenewalDate}
                onChange={(e) => onChange(patchExtra(person, { idExpiryDate: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idExpiryDate'))}
                title="State renewal date — leave blank for perpetual (LLC / Corp / Partnership in MO)"
              />
              <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 2 }}>
                Leave blank for perpetual entities.
              </div>
            </div>
          </div>

          {/*
            TODO(bill): SoS renewal-date semantics unclear for biennial-report
            states (MO Partnership + NonProfit file biennial reports). Does
            Field 258 store the renewal-report-due date or stay blank? Flagged
            in business.md §"Open questions" #3.
          */}
        </div>
      </CollapsibleSection>
    </div>
  );
}
